// Form Component to Add or Edit project task configuration

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select, Space } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../components/form/form";
import { FormRow } from "../../../../components/form/form-row";
import {
  TDropdownOptions,
  TConfigTask,
  TResponseModel,
} from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  AddEditConfigGroup,
  AddEditProjectCategoryWorkMapping,
  AddEditProjectWorkMapping,
  GetProductList,
  GetProjectCategoryList,
  GetProjectList,
  GetSpecificConfigGroup,
  GetSpecificProjectCategory,
  GetWorkCategoryList,
} from "../../../../utils/api.constant";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import CustomDropdown from "../../../../components/custom-dropdown/custom-dropdown";
import { PageSize } from "../../../../utils/constants";

type TDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {TDrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function ProjectCategoryAndWorkCategoryForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [selectedTask, setSelectedTask] = useState<string[]>([]);
  const [taskOptions, setTaskOptions] = useState<TDropdownOptions[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast

  // Hook to handle on load operations
  useEffect(() => {
    getTaskList();
    specificProjectCategory();
  }, []);

  // Handle on submit event
  const onFinish = async (values: TConfigTask) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditProjectCategoryWorkMapping, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTaskList = async () => {
    try {
      const requestPayload = {
        data: {
          pageSize: PageSize,
          currentPage: 1,
        },
      };

      const response: TResponseModel = await post(
        GetWorkCategoryList,
        requestPayload
      );
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setTaskOptions(
          response.data.map((item: any) => ({
            label: item.name,
            value: item.id,
          }))
        );
        setSelectedTask([response.data[0].id]);
      } else {
        onShowToast({
          type: "error",
          title: <CloseOutlined />,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const specificProjectCategory = async () => {
    if (props.id) {
      try {
        const payload = {
          data: {
            id: props.id,
          },
        };
        setIsLoading(true);
        const response = await post(GetSpecificProjectCategory, payload);
        if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
          const { data } = response;
          const workCategoryIds =
            data.workCategory?.map((status: { id: number }) => status.id) || [];
          const formValues = {
            projectCategoryId: data.id,
            workCategoryIds,
          };
          form.setFieldsValue(formValues);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: response.dataResponse.description,
          });
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <FormLayout>
      <Form
        form={form}
        name='register'
        onFinish={onFinish}
        layout='vertical'
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          {/* <Row gutter={16}>
            <Space>
              <Col span={24}>
                <h2 style={{ color: "#fff", marginTop: "10px" }}>
                  {`${props.id ? "Edit" : "Add"} a Task Configuration`}
                </h2>
              </Col>
            </Space>
          </Row> */}
          <Row
            gutter={16}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='projectCategoryId'
                label='Project Category'
                rules={[
                  {
                    required: true,
                    message: "enter project category",
                  },
                ]}
              >
                <CustomDropdown
                  endPoint={GetProjectCategoryList}
                  value={form.getFieldValue("projectCategory")}
                  selectedName={form.getFieldValue("projectCategory")}
                  onChange={(selected: any) => {
                    form.setFieldValue("projectCategoryId", selected.value);
                    form.setFieldValue("projectCategory", selected.label);
                  }}
                  label=''
                  placeHolder='Select project category'
                ></CustomDropdown>
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            className='formRowHeight'
          >
            <Col span={24}>
              <Form.Item
                name='workCategoryIds'
                label='Work Category'
                rules={[
                  {
                    required: true,
                    message: "Please select at least one Status",
                  },
                ]}
              >
                <Select
                  mode='multiple'
                  placeholder='Select status'
                  style={{ width: "100%" }}
                >
                  {taskOptions.map((task) => (
                    <Select.Option
                      key={task.value}
                      value={task.value}
                    >
                      {task.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className='formFooter'>
            <Button
              type='default'
              disabled={isLoading}
              className='btnDimensions'
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              className='btnDimensions'
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
